import Web3Modal from "web3modal";
import { ethers } from "ethers";
import {
  Button,
  Col,
  Container,
  Row,
  Spinner,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { Dispatch, useContext, useEffect, useState } from "react";
import BlockpartyContract from "@src/core/hardhat/artifacts/contracts/BlockpartyContract.sol/BlockpartyContract.json";
import { Asset } from "@src/core/models/asset";
import { Offer } from "@src/core/models/offer";
import { Order, OrderStatus } from "@src/core/models/order";
import { BloctoWalletContext } from "@src/core/contexts/blocto-wallet";
import { MetaMaskWalletContext } from "@src/core/contexts/meta-mask-wallet";
import { formatEth, formatUsd } from "@src/core/support/format-currency";
import { ChainType, Wallet } from "@src/core/models/wallet";
import { HttpClient } from "@src/core/api/http-client";
import { Transaction, TransactionStatus } from "@src/core/models/transaction";
import endpoints from "@src/core/api/endpoints";
import { WALLET_OPEN_MODAL_BUTTON_ID } from "@src/presentation/components/wallet/open-modal-button";
import OfferContentPreview from "@src/presentation/components/offers/content-preview";
import * as Styles from "./styles";
import { EthereumRateContext } from "@/src/core/contexts/ethereum-rate";

interface Props {
  asset: Asset;
  offer: Offer;
  order: Order;
}

const OrdersCheckout = ({ asset, offer, order }: Props): JSX.Element => {
  const history = useHistory();
  const { bloctoWallet } = useContext(BloctoWalletContext);
  const { metaMaskWallet } = useContext(MetaMaskWalletContext);
  const currentWallet = bloctoWallet || metaMaskWallet;
  const hasEnoughBalance = offer.price <= (metaMaskWallet?.balance || 0);
  const formattedEthValue = formatEth(offer.price);
  const [showNetworkAlert, setShowNetworkAlert] = useState<boolean>(false);
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [showLoadingMessage, setShowLoadingMessage] = useState<boolean>(false);
  const { ethereumRate } = useContext(EthereumRateContext);

  useEffect(() => {
    setShowNetworkAlert(false);
  }, [currentWallet]);

  const renderAddFundsButton = (): JSX.Element => {
    return (
      <Button
        type="button"
        onClick={() => history.push(`/checkout-add-funds/${order.id}`)}
      >
        <span>Add funds with credit card</span>
      </Button>
    );
  };

  const renderPurchaseButton = (): JSX.Element => {
    return (
      <Button
        type="button"
        disabled={!hasEnoughBalance}
        onClick={() => {
          if (!currentWallet) return;

          const isMainnet = process.env.REACT_APP_IS_MAINNET || "false";
          const invalidNetworkSelected =
            JSON.parse(isMainnet) &&
            currentWallet.chainId !== ChainType.Mainnet;

          if (invalidNetworkSelected) {
            setShowNetworkAlert(true);
            return;
          }

          submitOrder(
            order,
            offer,
            asset,
            currentWallet,
            setShowOverlay,
            setShowLoadingMessage,
            (transactionId: string, transactionHash: string) =>
              updateTransactionHash(transactionId, transactionHash)
          );
        }}
      >
        <span>{`Buy now for ${formattedEthValue}`}</span>
      </Button>
    );
  };

  const renderEditionMessage = (): string => {
    if (offer.assetIds.length > 1) {
      return `${asset.editionId} of ${offer.assetIds.length}`;
    }

    return "Unique edition";
  };

  const renderConnectWalletButton = (): JSX.Element => {
    return (
      <Button type="button" onClick={() => triggerWalletConnectModal()}>
        <span>Connect wallet to buy now</span>
      </Button>
    );
  };

  const updateTransactionHash = (
    transactionId: string,
    transactionHash: string
  ) => {
    const updatedOrder = {
      ...order,
      status: OrderStatus.Finished,
    };

    HttpClient.put<Order>(
      endpoints.ordersUpdate(order.id),
      updatedOrder,
      () => {
        const updatedTransaction = {
          orderId: order.id,
          transactionHash,
        };

        HttpClient.put<Transaction>(
          endpoints.transactionsUpdate(transactionId),
          updatedTransaction,
          () => {
            history.push(`/checkout-success/${transactionHash}`);
          }
        );
      }
    );
  };

  return (
    <Styles.Wrapper>
      <ToastContainer className="p-3" position="top-center">
        <Toast bg="danger" show={showNetworkAlert}>
          <Toast.Body className="text-white">
            Please, connect to the Mainnet network
          </Toast.Body>
        </Toast>
      </ToastContainer>
      {showOverlay && (
        <Styles.Overlay>
          {showLoadingMessage && (
            <>
              <Styles.OverlayLoadingWrapper>
                <Spinner animation="grow" />
                <Styles.OverlayLoadingMessage>
                  Please wait a few seconds while your request is being
                  processed.
                </Styles.OverlayLoadingMessage>
              </Styles.OverlayLoadingWrapper>
            </>
          )}
        </Styles.Overlay>
      )}
      <Container>
        <Row>
          <Col sm={12} lg={3}>
            <OfferContentPreview offer={offer} />
          </Col>
          <Col sm={12} lg={9}>
            <Styles.ContentWrapper>
              <Styles.Title>{offer.name}</Styles.Title>
              <p>{offer.description}</p>
              <Styles.TagsWrapper>
                <div>
                  <Styles.TagLabel>Edition</Styles.TagLabel>
                  <Styles.TagText>{renderEditionMessage()}</Styles.TagText>
                </div>
                <div>
                  <Styles.TagLabel>ETH Price</Styles.TagLabel>
                  <Styles.TagText>{formatEth(offer.price)}</Styles.TagText>
                </div>
                <div>
                  <Styles.TagLabel>USD Price</Styles.TagLabel>
                  <Styles.TagText>
                    {ethereumRate
                      ? formatUsd(
                          Number(ethereumRate.currentRate) * offer.price
                        )
                      : "-"}
                  </Styles.TagText>
                </div>
              </Styles.TagsWrapper>
              <Col sm={12} lg={6}>
                <Styles.CallToActionWrapper>
                  {currentWallet && renderPurchaseButton()}
                  {/* {currentWallet && renderAddFundsButton()} */}
                  {!currentWallet && renderConnectWalletButton()}
                </Styles.CallToActionWrapper>
              </Col>
            </Styles.ContentWrapper>
          </Col>
        </Row>
      </Container>
    </Styles.Wrapper>
  );
};

const submitOrder = async (
  order: Order,
  offer: Offer,
  selectedAsset: Asset,
  wallet: Wallet,
  setShowOverlay: Dispatch<boolean>,
  setShowLoadingMessage: Dispatch<boolean>,
  onSuccess: (transactionId: string, transactionHash: string) => void
): Promise<void> => {
  const web3Modal = new Web3Modal();
  const connection = await web3Modal.connect();
  const provider = new ethers.providers.Web3Provider(connection);
  const buyer = provider.getSigner();
  const contractAddress = process.env.REACT_APP_CONTRACT_ADDRESS || "";
  const contract = new ethers.Contract(
    contractAddress,
    BlockpartyContract.abi,
    buyer
  );
  setShowOverlay(true);
  setShowLoadingMessage(true);

  const platformFeePercent = 5;

  createTransaction(order, wallet, async (response: any) => {
    const voucherResponse = response?.voucher;
    const transactionId = response?.transaction_id;

    setShowLoadingMessage(false);

    const metaMaskTransaction = await contract
      .redeem(platformFeePercent, voucherResponse, {
        value: voucherResponse.price,
      })
      .catch((error: any) => {
        console.log(error);
      });

    if (!metaMaskTransaction) {
      setShowOverlay(false);
      setShowLoadingMessage(false);
      return undefined;
    }

    setShowLoadingMessage(true);

    onSuccess(transactionId, metaMaskTransaction.hash);

    return metaMaskTransaction.wait().catch((error: any) => {
      console.log(error);
      setShowOverlay(false);
      setShowLoadingMessage(false);
    });
  });
};

const createTransaction = (
  order: Order,
  currentWallet: Wallet,
  onSuccess: (response: any) => void
) => {
  const payload = {
    assetId: order.assetId,
    block: undefined,
    blockIndex: undefined,
    gasFees: undefined,
    network: undefined,
    offerId: order.offerId,
    orderId: order.id,
    status: TransactionStatus.AwatingConfirmation,
    timestamp: undefined,
    transactionHash: undefined,
    wallet: currentWallet.id,
    skipSignature: false,
  };

  HttpClient.post<Transaction>(
    endpoints.transactionsCreate(),
    payload,
    (response) => {
      onSuccess(response);
    }
  );
};

const triggerWalletConnectModal = () => {
  const $openModalButton = document.getElementById(WALLET_OPEN_MODAL_BUTTON_ID);

  $openModalButton?.click();
};

export default OrdersCheckout;
